// ============================================================================
// FNE-BRIDGE Frontend - Service Authentification
// ============================================================================

import apiClient from './apiClient';
import type {
  LoginRequest, LoginResponse, ValidateOtpRequest, AuthResponse,
  ChangePasswordRequest, ForgotPasswordRequest, ApiResponse,
  ResetPasswordRequestResponse, ResetPasswordVerifyOtpResponse,
} from '../types/api';
import type { User } from '../types/models';

const AUTH_BASE = '/auth';

export const authService = {
  login: (data: LoginRequest) =>
    apiClient.post<ApiResponse<LoginResponse>>(`${AUTH_BASE}/login`, data),

  validateOtp: (data: ValidateOtpRequest) =>
    apiClient.post<ApiResponse<AuthResponse>>(`${AUTH_BASE}/validate-otp`, data),

  refreshToken: (refreshToken: string) =>
    apiClient.post<ApiResponse<AuthResponse>>(`${AUTH_BASE}/refresh-token`, { refresh_token: refreshToken }),

  changePassword: (data: ChangePasswordRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>(`${AUTH_BASE}/change-password`, data),

  resetPassword: (data: ForgotPasswordRequest) =>
    apiClient.post<ApiResponse<ResetPasswordRequestResponse>>(`${AUTH_BASE}/reset-password`, data),

  resetPasswordVerifyOtp: (reset_session_id: string, code: string) =>
    apiClient.post<ApiResponse<ResetPasswordVerifyOtpResponse>>(`${AUTH_BASE}/reset-password/verify-otp`, { reset_session_id, code }),

  resetPasswordConfirm: (reset_token: string, nouveau_mot_de_passe: string, confirmation: string) =>
    apiClient.post<ApiResponse<{ message: string }>>(`${AUTH_BASE}/reset-password/confirm`, { reset_token, nouveau_mot_de_passe, confirmation }),

  getMe: () =>
    apiClient.get<ApiResponse<User>>(`${AUTH_BASE}/me`),

  logout: () =>
    apiClient.post<ApiResponse<{ message: string }>>(`${AUTH_BASE}/logout`),

  // --- Création de compte ---
  createAccount: (data: {
    raison_sociale: string; ncc: string; email: string; telephone_mobile: string;
    regime_fiscal: string; secteur_activite_entreprise?: string;
    direction_rattachement?: string; centre_impots?: string;
  }) =>
    apiClient.post<ApiResponse<{ pending_id: string }>>(`${AUTH_BASE}/create-account`, data),

  createAccountSendOtp: (pending_id: string, method: 'email' | 'sms' | 'whatsapp') =>
    apiClient.post<ApiResponse<{ method: string; masked_email: string; masked_phone: string; otp_debug?: string }>>(
      `${AUTH_BASE}/create-account/send-otp`, { pending_id, method }
    ),

  createAccountVerifyOtp: (pending_id: string, code: string) =>
    apiClient.post<ApiResponse<{ message: string }>>(`${AUTH_BASE}/create-account/verify-otp`, { pending_id, code }),

  createAccountSetPassword: (pending_id: string, activation_key: string, mot_de_passe: string, confirmation: string) =>
    apiClient.post<ApiResponse<{ ncc: string; raison_sociale: string }>>(
      `${AUTH_BASE}/create-account/set-password`, { pending_id, activation_key, mot_de_passe, confirmation }
    ),

  validateActivationKey: (cle: string) =>
    apiClient.post<ApiResponse<{ valid: boolean; max_utilisateurs: number | null; duree_jours: number }>>(
      `${AUTH_BASE}/create-account/validate-key`, { cle }
    ),

  requestActivationKey: (data: {
    raison_sociale: string; ncc: string; email: string; telephone_mobile: string;
    tranche_utilisateurs: string; regime_fiscal?: string; secteur_activite_entreprise?: string;
  }) =>
    apiClient.post<ApiResponse<{ message: string }>>(
      `${AUTH_BASE}/create-account/request-key`, data
    ),
};
